import ContinuationDate from './ContinuationDate';
import CalendarSurfaceItemBase from './itemTemplates/CalendarSurfaceItem.base';
import { getAllDayStyles } from './itemTemplates/CalendarSurfaceItem.styles';
import { observer } from 'mobx-react-lite';
import { Icon } from '@fluentui/react/lib/Icon';
import { styled } from '@fluentui/react/lib/Utilities';
import {
    getCalendarEventItemSubjectText,
    getCalendarEventItemLocationText,
} from 'owa-calendar-event-attributes';
import { ControlIcons } from 'owa-control-icons';
import { differenceInMilliseconds } from 'owa-datetime';
import { isCurrentCultureRightToLeft } from 'owa-localize';
import TimeConstants from 'owa-datetime-utils/lib/TimeConstants';
import React from 'react';
import type {
    CalendarSurfaceItemContentProps,
    CalendarSurfaceItemProps,
} from './itemTemplates/CalendarSurfaceItem.types';
import {
    renderIconWrapperAllDayItem,
    renderCharmWrapperAllDayItem,
} from './CalendarSurfaceItemComponentRenderer';
import { getCalendarEventDateRangeVisualDateRange } from 'owa-calendar-events-visual-date-range';

import classNames from 'classnames';
import surfaceItemStyles from './CalendarAllDaySurfaceItem.scss';

const DefaultCalendarAllDaySurfaceItem = observer(function CalendarAllDaySurfaceItem(
    props: CalendarSurfaceItemProps
) {
    function renderContent(contentProps: CalendarSurfaceItemContentProps) {
        const { item, colorScheme, charmId, itemState, allowEmptySubject } = contentProps;
        const subject = getCalendarEventItemSubjectText(item, allowEmptySubject);
        const location = getCalendarEventItemLocationText(item);
        const organizer = item.Organizer && item.Organizer.Mailbox.Name;

        return (
            <>
                <div className={classNames(surfaceItemStyles.itemContent)}>
                    {itemState?.continuesLeft && (
                        <div className={classNames(surfaceItemStyles.continuationWrapper)}>
                            <Icon
                                iconName={
                                    isCurrentCultureRightToLeft()
                                        ? ControlIcons.ChevronRight
                                        : ControlIcons.ChevronLeft
                                }
                                className={classNames(
                                    surfaceItemStyles.icon,
                                    surfaceItemStyles.continuation
                                )}
                            />
                            <ContinuationDate date={item.Start} />
                        </div>
                    )}
                    <h2 className={surfaceItemStyles.itemSubject}>
                        {renderCharmWrapperAllDayItem(item, colorScheme, charmId)}
                        <span>{subject}</span>
                        {location && (
                            <span className={surfaceItemStyles.itemElementContent}>{location}</span>
                        )}
                        {item.IsMeeting && organizer && (
                            <span className={surfaceItemStyles.itemElementContent}>
                                {organizer}
                            </span>
                        )}
                    </h2>
                </div>
                {renderIconWrapperAllDayItem(item, itemState)}
                {itemState?.continuesRight && (
                    <div
                        className={classNames(surfaceItemStyles.continuationRightWrapper)}
                        key={'continuesRightIcon'}>
                        <ContinuationDate
                            date={
                                getCalendarEventDateRangeVisualDateRange({
                                    start: item.Start,
                                    end: item.End,
                                }).end
                            }
                        />
                        <Icon
                            iconName={
                                isCurrentCultureRightToLeft()
                                    ? ControlIcons.ChevronLeft
                                    : ControlIcons.ChevronRight
                            }
                            className={classNames(
                                surfaceItemStyles.icon,
                                surfaceItemStyles.continuation
                            )}
                        />
                    </div>
                )}
            </>
        );
    }

    const { item, itemState, increaseFontSize } = props;
    const hideFBBar =
        item.FreeBusyType == 'Free' || item.IsCancelled || item.ResponseType == 'Decline';
    const isMultiDay =
        differenceInMilliseconds(item.End, item.Start) > TimeConstants.MillisecondsInOneDay;
    const hideBorder =
        hideFBBar &&
        !(isMultiDay || (itemState && (itemState.continuesLeft || itemState.continuesRight)));

    return (
        <CalendarSurfaceItemBase
            {...props}
            className={classNames(props.className, {
                [surfaceItemStyles.increaseFontSize]: increaseFontSize,
                [surfaceItemStyles.itemIsCancelled]:
                    item.ResponseType === 'Decline' || item.IsCancelled,
            })}
            onRender={renderContent}
            hideFreeBusyBar={hideFBBar}
            hideBorder={hideBorder}
        />
    );
});

const CalendarAllDaySurfaceItem = styled(DefaultCalendarAllDaySurfaceItem, getAllDayStyles);
export default CalendarAllDaySurfaceItem;
